<template>
  <modal
    @hidden="onHidden"
    auto-overflow
    class="bundle-wizard uk-modal-container"
    ref="modal"
    title="Bundle-Assistent"
  >
    <alert-box
      icon="info"
      v-if="allBundlesExist"
    >{{$t('hints.allBundlesExist')}}</alert-box>

    <alert-box
      icon="bolt"
      type="danger"
      v-if="hasErrors"
    >{{$t('hints.errorsOccurred')}}</alert-box>

    <table class="uk-table uk-table-justify uk-table-divider">
      <thead>
        <tr>
          <th
            :key="bundling.position"
            :style="{ width: columnWidth }"
            class="bundle-wizard__cell"
            colspan="2"
            v-for="bundling in bundleDefinitions"
          >{{bundling.productFamily.name}}</th>
          <th
            class="bundle-wizard__cell bundle-wizard__cell--input"
          >{{$t('fields.bundleArticleNumber')}}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          :key="combination.articleNumber"
          v-for="combination in bundleCombinations"
        >
          <template v-for="product in combination.products">
            <td
              :class="{ 'bundle-wizard__cell--disabled': !combination.articleNumber }"
              :key="product.articleNumber"
              class="bundle-wizard__cell bundle-wizard__cell--thumbnail"
            >
              <router-link
                :to="{ name: 'product', params: { id: product.id } }"
                tabindex="-1"
                target="_blank"
                v-if="product.teaserImage"
              >
                <img
                  :alt="product.name"
                  :src="product.teaserImage.thumbImageUrl"
                />
              </router-link>
              <text-placeholder v-else />
            </td>
            <td
              :class="{ 'bundle-wizard__cell--disabled': !combination.articleNumber }"
              :key="product.articleNumber"
              :style="{ width: columnWidth }"
              class="bundle-wizard__cell"
            >
              <router-link
                :to="{ name: 'product', params: { id: product.id } }"
                tabindex="-1"
                target="_blank"
              >{{product.variantName || product.name}}</router-link>
              <div class="uk-text-muted">{{product.articleNumber}}</div>
            </td>
          </template>
          <td class="bundle-wizard__cell bundle-wizard__cell--input">
            <span
              class="uk-text-success"
              v-if="combination.exists"
            >{{$t('hints.bundleExists', { articleNumber: combination.articleNumber })}}</span>
            <form-group :errors="combination.errors" :label="false" v-else>
              <input
                :class="{ 'uk-form-danger': combination.errors.length > 0 }"
                @input="combination.errors = []"
                @keyup.enter="insertBundleProducts"
                class="uk-input"
                type="text"
                v-model="combination.articleNumber"
              />
            </form-group>
          </td>
        </tr>
      </tbody>
    </table>

    <common-button
      :disabled="submitDisabled"
      @click="insertBundleProducts"
      slot="buttons"
      type="primary"
    >{{$t('actions.createBundles', { count: createdBundleCount })}}</common-button>
  </modal>
</template>

<script>
import { isEqual, product as permutations } from 'lodash';
import CommonButton from '@/components/CommonButton';
import FormGroup from '@/components/FormGroup';
import insertBundleProductMutation
  from '@/api/mutations/insert-bundle-product.graphql';
import Modal from '@/components/Modal';
import TextPlaceholder from '@/components/TextPlaceholder';
import ValidatedMutation from '@/api/validated-mutation';

function getBundleCombinations(productFamily) {
  const productsByBundlePart = productFamily.bundlings.map(
    d => d.productFamily.products,
  );
  const combinations = [];

  permutations(...productsByBundlePart).forEach((products, index) => {
    const bundledProductIds = products.map(p => p.id);

    const bundle = productFamily.products.find((bundleProduct) => {
      const productIds = bundleProduct.bundlings.map(b => b.product.id);
      return isEqual(productIds, bundledProductIds);
    });

    let articleNumber;
    let name;
    if (bundle) {
      articleNumber = bundle.articleNumber;
      name = bundle.name;
    } else {
      name = products.map(p => p.name).join(' + ');
    }

    combinations.push({
      index,
      articleNumber,
      bundle,
      exists: !!bundle,
      name,
      products,
      errors: [],
    });
  });

  return combinations;
}

export default {
  name: 'bundle-wizard',
  components: {
    CommonButton,
    FormGroup,
    Modal,
    TextPlaceholder,
  },
  props: {
    productFamily: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      bundleCombinations: getBundleCombinations(this.productFamily),
      submitting: 0,
      updateOnClose: false,
    };
  },
  watch: {
    productFamily() {
      this.reset();
    },
  },
  computed: {
    hasErrors() {
      return this.bundleCombinations.some(c => c.errors.length > 0);
    },
    allBundlesExist() {
      return this.bundleCombinations.every(c => c.exists);
    },
    columnWidth() {
      const value = (100.0 / this.bundleDefinitions.length).toString();
      return `${value}%`;
    },
    bundleDefinitions() {
      return this.productFamily.bundlings || [];
    },
    isSubmitting() {
      return this.submitting > 0;
    },
    submitDisabled() {
      return this.isSubmitting || !this.hasArticleNumbersDefined;
    },
    createdBundleCount() {
      return this.bundleParams.length;
    },
    hasArticleNumbersDefined() {
      return this.bundleCombinations.some(c => !c.exists && c.articleNumber);
    },
    bundleParams() {
      const results = [];

      this.bundleCombinations.forEach((combination, index) => {
        if (combination.exists || !combination.articleNumber) return true;

        const bundlings = [];
        this.bundleDefinitions.forEach((definition, definitionIndex) => {
          const product = combination.products[definitionIndex];
          bundlings.push({
            definitionId: definition.id,
            productId: product.id,
          });
        });

        const params = {
          familyId: this.productFamily.id,
          name: combination.name,
          articleNumber: combination.articleNumber,
          bundlings,
        };

        results.push({ index, params });
        return true;
      });

      return results;
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    reset() {
      this.bundleCombinations = getBundleCombinations(this.productFamily);
    },
    onHidden() {
      this.reset();
      if (this.updateOnClose) {
        this.$emit('update');
        this.updateOnClose = false;
      }
    },
    async insertBundleProducts() {
      const results = await Promise.all(
        this.bundleParams.map(params => this.insertBundleProduct(params)),
      );

      let allAreValid = true;
      let anyIsValid = false;

      results.forEach((result) => {
        const combination = this.bundleCombinations[result.index];

        if (result.isValid) {
          anyIsValid = true;
          combination.bundle = result.product;
          combination.errors = [];
          combination.exists = true;
        } else {
          allAreValid = false;
          combination.errors = result.errors.articleNumber;
        }
      });

      if (anyIsValid) this.updateOnClose = true;
      if (allAreValid) this.hide();

      return results;
    },
    async insertBundleProduct({ index, params }) {
      this.submitting += 1;
      try {
        const result = await ValidatedMutation.mutate({
          mutation: insertBundleProductMutation,
          variables: { product: params },
        });
        this.submitting -= 1;
        return {
          index,
          params,
          isValid: result.isValid,
          errors: result.errors,
        };
      } catch (error) {
        this.submitting -= 1;
        throw error;
      }
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          createBundles: '{count} Bundle(s) erzeugen',
        },
        fields: {
          bundleArticleNumber: 'Bundle-Artikelnummer',
        },
        hints: {
          allBundlesExist:
            'Alle möglichen Bundles für diese Bundlefamilie wurden erzeugt.',
          bundleExists: 'Bundle existiert: {articleNumber}',
          errorsOccurred: 'Nicht alle Bundles konnten erstellt werden.',
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
.bundle-wizard__cell {
  &--disabled {
    opacity: 0.6;
  }

  &--thumbnail {
    min-width: 44px;
    width: 44px;
  }

  &--input {
    min-width: 250px;
    width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// BundleWizard.vue?5a4c6639